'use client';

import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useContextSelector } from 'use-context-selector';

import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import {
  backdropTransitionConfig,
  inspectTransitionConfig,
} from '@/ux/features/website-assembler/transitions/InspectTransition';
import { cn } from '@/ux/utils';
import { useScrollLock } from '@/ux/utils/useScrollLock';

import { initializeInspectViews, InspectViewRegistry } from '../registries';
import {
  ContentTransition,
  HistoryPanel,
  NavigationBar,
  NavigationProvider,
  useInspectNavigation,
} from './InspectViewNavigator';

interface InspectViewRouterEnhancedProps {
  isOpen: boolean;
  onClose: () => void;
  content: any;
  contentType: 'blog' | 'product' | 'image' | string;
  onAction?: (action: string, content: any, data?: any) => void;
}

// Inner component that uses navigation
const InspectViewContent: React.FC<{
  content: any;
  contentType: string;
  onClose: () => void;
  onAction?: (action: string, content: any, data?: any) => void;
}> = ({ content, contentType, onClose, onAction }) => {
  const [currentContent, setCurrentContent] = useState(content);
  const [currentType, setCurrentType] = useState(contentType);
  const [showHistory, setShowHistory] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const nav = useInspectNavigation();
  const auth = useContextSelector(AppContext, authSelector);
  const isUserLoggedIn = auth?.loggedIn;

  // Handle navigation from ambient intelligence
  useEffect(() => {
    const handleContentSelect = (event: CustomEvent) => {
      const { content: newContent, type } = event.detail;

      // Extract key info for navigation
      const navItem = {
        type: type as any,
        content: newContent,
        title: newContent.title || 'Untitled',
        thumbnail: newContent.media?.thumbnail?.url || newContent.media?.hero?.url,
      };

      // Push to navigation history
      nav.push(navItem);

      // Update current view
      setCurrentContent(newContent);
      setCurrentType(type);
    };

    window.addEventListener('inspectViewNavigate' as any, handleContentSelect);
    return () => {
      window.removeEventListener('inspectViewNavigate' as any, handleContentSelect);
    };
  }, [nav]);

  // Handle navigation changes
  const handleNavigate = useCallback((item: any) => {
    setCurrentContent(item.content);
    setCurrentType(item.type);
  }, []);

  // Add initial content to history on mount
  useEffect(() => {
    if (content && contentType) {
      nav.push({
        type: contentType as any,
        content,
        title: content.title || 'Untitled',
        thumbnail: content.media?.thumbnail?.url || content.media?.hero?.url,
      });
    }
  }, []); // Only run on mount

  // Update current view when props change
  useEffect(() => {
    setCurrentContent(content);
    setCurrentType(contentType);
  }, [content, contentType]);

  // Ensure inspect views are initialized
  useEffect(() => {
    initializeInspectViews();
  }, []);

  const renderContent = () => {
    // Use InspectViewRegistry which handles both content type and cardType
    return InspectViewRegistry.render(currentContent, {
      onClose,
      onAction,
    });
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const containerClass = cn(
    'fixed bg-white dark:bg-gray-900 shadow-2xl flex flex-col',
    isMobile || isFullScreen
      ? 'inset-0'
      : 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-xl overflow-hidden',
  );

  const containerStyle =
    isMobile || isFullScreen
      ? {
          zIndex: 10000,
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
        }
      : {
          zIndex: 10000,
          width: '90vw',
          maxWidth: '1200px',
          height: '90vh',
          maxHeight: '900px',
        };

  return (
    <motion.div
      className={containerClass}
      style={containerStyle}
      initial={{
        ...inspectTransitionConfig.initial,
        ...(isMobile || isFullScreen ? { x: 0, y: 0 } : { x: '-50%', y: '-50%' }),
      }}
      animate={{
        ...inspectTransitionConfig.animate,
        ...(isMobile || isFullScreen ? { x: 0, y: 0 } : { x: '-50%', y: '-50%' }),
      }}
      exit={{
        ...inspectTransitionConfig.exit,
        ...(isMobile || isFullScreen ? { x: 0, y: 0 } : { x: '-50%', y: '-50%' }),
      }}
      transition={inspectTransitionConfig.transition}
    >
      {/* Navigation Bar */}
      <NavigationBar
        onClose={onClose}
        showHistoryPanel={showHistory}
        onToggleHistory={() => setShowHistory(!showHistory)}
        onToggleFullscreen={() => setIsFullScreen(!isFullScreen)}
        isFullscreen={isFullScreen}
        isMobile={isMobile}
      />

      {/* Content Area with History Panel */}
      <div className="flex-1 relative overflow-y-auto">
        {/* Main Content */}
        <ContentTransition contentKey={`${currentType}-${currentContent?.id}`}>
          <div className="h-full">{renderContent()}</div>
        </ContentTransition>

        {/* History Panel */}
        <HistoryPanel
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          onSelectItem={handleNavigate}
        />
      </div>
    </motion.div>
  );
};

export const InspectViewRouterEnhanced: React.FC<InspectViewRouterEnhancedProps> = ({
  isOpen,
  onClose,
  content,
  contentType,
  onAction,
}) => {
  const [mounted, setMounted] = useState(false);
  useScrollLock(isOpen);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted || !isOpen) return null;

  const handleClose = () => {
    // Clear navigation history on close
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            {...backdropTransitionConfig}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            style={{ zIndex: 9999 }}
            onClick={handleClose}
          />

          {/* Modal with Navigation Provider */}
          <NavigationProvider onNavigate={() => {}}>
            <InspectViewContent
              content={content}
              contentType={contentType}
              onClose={handleClose}
              onAction={onAction}
            />
          </NavigationProvider>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};
